// React imports
import { useEffect, useMemo, useState } from "react";

// Material UI imports
import { MenuItem, Box, FormControlLabel } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Add } from "@mui/icons-material";

// ERP Common imports
import Select from "@/components/select/select";
import Button from "@/components/button/button";
import Typography from "@/components/typography/typography";
import TextField from "@/components/text-field/text-field";
import Checkbox from "@/components/checkbox/checkbox";
import { generateRandomId } from "@/utils/common";
import { SECTION_TYPES } from "@/utils/constant";

// Local imports
import { ISwitcherForms } from "../field_properties";
import "../custom-form.scss";

const SectionEditForm = (props: any) => {
  const { handlePropertyValueUpdate } = props;
  const [elementJSON, setElementJSON] = useState(props.element);
  const [addForm, setAddForm] = useState<boolean>(false);
  const [newForm, setNewForm] = useState<string>("");
  const selectOption = ["section"];

  const isDuplicateFormName = useMemo(
    () =>
      elementJSON.switcher_forms?.map((ele: any) => ele.name).includes(newForm),
    [elementJSON.switcher_forms, newForm]
  );

  useEffect(() => {
    setElementJSON(props.element);
  }, [props.element]);

  return (
    <Box className="formSection--FieldContainer" key={elementJSON.id}>
      <div>
        <Select
          key={elementJSON.id}
          fullWidth
          placeholder="Select Field"
          size="small"
          disabled
          defaultValue={elementJSON.field_type}
          label="Field Type"
          variant="outlined"
        >
          {selectOption?.map((item) => (
            <MenuItem value={item}>{item}</MenuItem>
          ))}
        </Select>
      </div>
      <div>
        <Select
          key={elementJSON.id}
          fullWidth
          placeholder="Select Field"
          size="small"
          name="section_type"
          defaultValue={elementJSON.section_type}
          label="Section Type"
          variant="outlined"
          onChange={(e: any) => handlePropertyValueUpdate(e)}
        >
          {Object.values(SECTION_TYPES).map((type) => {
            return (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            );
          })}
        </Select>
      </div>
      <div>
        <TextField
          fullWidth
          label="Label"
          is_required
          key={elementJSON.id}
          name="label"
          type="text"
          onChange={handlePropertyValueUpdate}
          defaultValue={elementJSON.label}
        />
      </div>
      <FormControlLabel
        key={elementJSON.id}
        name="is_accordion"
        control={<Checkbox />}
        onChange={handlePropertyValueUpdate}
        checked={elementJSON.is_accordion}
        label="Is Accordion"
      />
      {elementJSON.section_type === SECTION_TYPES.FORM_SWITCH_SECTION ? (
        <div className="formSection--SubFieldContainer">
          <div>
            <TextField
              fullWidth
              label="Form label"
              is_required
              key={elementJSON.id}
              name="form_switcher_label"
              type="text"
              onChange={handlePropertyValueUpdate}
              defaultValue={elementJSON.form_switcher_label}
            />
          </div>
          <div>
            <Typography
              type="s5"
              weight={"medium"}
              color={"theme.secondary.800"}
            >
              Forms
            </Typography>
            {elementJSON?.switcher_forms?.map(
              (ele: ISwitcherForms) => (
                <div className="formSection--MultiOptions" key={elementJSON.id}>
                  <TextField
                    fullWidth
                    key={ele.id}
                    value={ele.name}
                    style={{ marginBottom: "10px" }}
                    name="switcher_forms"
                    onChange={(e: any) => {
                      const newVal = elementJSON.switcher_forms?.map(
                        (formEle: ISwitcherForms) =>
                          ele.id === formEle.id
                            ? { ...formEle, name: e.target.value }
                            : { ...formEle }
                      );

                      handlePropertyValueUpdate(e, newVal);
                    }}
                  />
                  <CloseIcon
                    style={{ fontSize: "1rem", color: "#656669" }}
                    onClick={(e: any) => {
                      const newVal = [...elementJSON.switcher_forms].filter(
                        (item) => item.id !== ele.id
                      );
                      e.target.name = "switcher_forms";
                      handlePropertyValueUpdate(e, newVal);
                    }}
                  />
                </div>
              )
            )}
          </div>
          {addForm ? (
            <div>
              <div
                className="formSection--MultiOptions formSection--FullWidth"
                key={elementJSON.id}
              >
                <div className="formSection--FullWidth">
                  <TextField
                    fullWidth
                    label="Form section name"
                    placeholder="Form section name"
                    is_required
                    key={elementJSON.id}
                    type="text"
                    value={newForm}
                    error={isDuplicateFormName}
                    helperText={
                      isDuplicateFormName
                        ? "Form section name can not duplicate"
                        : ""
                    }
                    onChange={(e: any) => setNewForm(e.target.value)}
                  />
                </div>
                <CloseIcon
                  style={{ fontSize: "1rem", color: "#656669" }}
                  onClick={() => {
                    setAddForm(false);
                    setNewForm("");
                  }}
                />
              </div>
              <Button
                onClick={(e: any) => {
                  const { uniqueStringId: id } = generateRandomId();
                  const newVal = [
                    ...elementJSON.switcher_forms,
                    { name: newForm, id },
                  ];
                  handlePropertyValueUpdate(e, newVal);
                  setNewForm("");
                  setAddForm(false);
                }}
                name="switcher_forms"
                variant="text"
                className="formSection--Button"
                disabled={isDuplicateFormName || !newForm}
                startIcon={<Add />}
              >
                Add
              </Button>
            </div>
          ) : null}
          {!addForm ? (
            <Button
              onClick={() => setAddForm(true)}
              variant="text"
              className="formSection--Button"
              startIcon={<Add />}
            >
              Add forms
            </Button>
          ) : null}
        </div>
      ) : null}
    </Box>
  );
};

export { SectionEditForm };
export default SectionEditForm;
